// BugPanel.jsx -- Popout bug/feature reporting panel (BUG-S1)
// Fixed side panel, toggled from bottom-right button in Layout
// Tabs: Queue | Ready | Verify | Closed
// Screenshot: paste (Cmd+V) or drag-and-drop onto form
// ASCII only. No Unicode. var declarations only.

import { useState, useEffect, useRef } from 'react';

var FONT = "'IBM Plex Sans', sans-serif";
var MONO = "'IBM Plex Mono', monospace";
var ACCENT = '#4F6EF7';

var STATUS_COLORS = {
  open:     '#EF4444',
  triaged:  '#F59E0B',
  fixing:   '#3B82F6',
  qa:       '#22C55E',
  verified: '#16A34A',
  closed:   '#6B7280'
};

export var TABS = [
  { id: 'queue',  label: 'Queue',  status: 'open' },
  { id: 'ready',  label: 'Ready',  status: 'triaged' },
  { id: 'verify', label: 'Verify', status: 'qa' },
  { id: 'closed', label: 'Closed', status: 'closed' }
];

var EMPTY_MSGS = {
  queue:  'No bugs in queue.',
  ready:  'No sessions ready to fire.',
  verify: 'Nothing to verify.',
  closed: 'No closed items.'
};

function isDarkTheme() {
  try { return document.documentElement.getAttribute('data-theme') === 'dark'; } catch(e) { return false; }
}

export default function BugPanel(props) {
  var visible = props.visible;
  var onClose = props.onClose;

  var _tab = useState('queue'); var tab = _tab[0]; var setTab = _tab[1];
  var _bugs = useState([]); var bugs = _bugs[0]; var setBugs = _bugs[1];
  var _loading = useState(false); var loading = _loading[0]; var setLoading = _loading[1];
  var _showForm = useState(false); var showForm = _showForm[0]; var setShowForm = _showForm[1];
  var _title = useState(''); var title = _title[0]; var setTitle = _title[1];
  var _desc = useState(''); var desc = _desc[0]; var setDesc = _desc[1];
  var _itemType = useState('bug'); var itemType = _itemType[0]; var setItemType = _itemType[1];
  var _project = useState('hub'); var project = _project[0]; var setProject = _project[1];
  var _assignee = useState(''); var assignee = _assignee[0]; var setAssignee = _assignee[1];
  var _filterProject = useState('all'); var filterProject = _filterProject[0]; var setFilterProject = _filterProject[1];
  var _filterType = useState('all'); var filterType = _filterType[0]; var setFilterType = _filterType[1];
  var _filterAssignee = useState('all'); var filterAssignee = _filterAssignee[0]; var setFilterAssignee = _filterAssignee[1];
  var _screenshot = useState(null); var screenshot = _screenshot[0]; var setScreenshot = _screenshot[1];
  var _submitting = useState(false); var submitting = _submitting[0]; var setSubmitting = _submitting[1];
  var _rejectId = useState(null); var rejectId = _rejectId[0]; var setRejectId = _rejectId[1];
  var _rejectNotes = useState(''); var rejectNotes = _rejectNotes[0]; var setRejectNotes = _rejectNotes[1];
  var _rejectShot = useState(null); var rejectShot = _rejectShot[0]; var setRejectShot = _rejectShot[1];
  var _tabCounts = useState({}); var tabCounts = _tabCounts[0]; var setTabCounts = _tabCounts[1];
  var _editId = useState(null); var editId = _editId[0]; var setEditId = _editId[1];
  var _editTitle = useState(''); var editTitle = _editTitle[0]; var setEditTitle = _editTitle[1];
  var _editDesc = useState(''); var editDesc = _editDesc[0]; var setEditDesc = _editDesc[1];
  var dropRef = useRef(null);

  useEffect(function() {
    if (visible || props.standalone) { loadBugs(); loadTabCounts(); }
  }, [visible, tab, filterProject, filterType, filterAssignee]);

  function loadBugs() {
    setLoading(true);
    var activeTab = null;
    for (var i = 0; i < TABS.length; i++) {
      if (TABS[i].id === tab) { activeTab = TABS[i]; break; }
    }
    var params = activeTab ? ('&status=' + activeTab.status) : '';
    if (filterProject !== 'all') params += '&project=' + filterProject;
    if (filterType !== 'all') params += '&type=' + filterType;
    if (filterAssignee !== 'all') params += '&assigned_agent=' + encodeURIComponent(filterAssignee);

    fetch('/api/bugs?' + params.slice(1))
      .then(function(r) { return r.json(); })
      .then(function(d) { setBugs(Array.isArray(d.data) ? d.data : []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  function submitBug() {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    fetch('/api/bugs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: title.trim(),
        description: desc.trim(),
        type: itemType,
        project: project,
        assigned_agent: assignee,
        page_url: window.location.pathname
      })
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.ok && screenshot && d.data && d.data.id) {
          return fetch('/api/bugs/' + d.data.id + '/screenshot', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: screenshot })
          }).then(function() { resetForm(); loadBugs(); loadTabCounts(); setSubmitting(false); });
        }
        resetForm();
        loadBugs();
        loadTabCounts();
        setSubmitting(false);
      })
      .catch(function() { setSubmitting(false); });
  }

  function resetForm() {
    setTitle('');
    setDesc('');
    setScreenshot(null);
    setAssignee('');
    setShowForm(false);
  }

  function updateBug(id, updates) {
    fetch('/api/bugs/' + id, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    }).then(function() { loadBugs(); loadTabCounts(); });
  }

  function loadTabCounts() {
    fetch('/api/bugs').then(function(r) { return r.json(); }).then(function(d) {
      var items = d.data || d.results || [];
      var counts = { open: 0, triaged: 0, qa: 0, closed: 0 };
      items.forEach(function(b) { if (counts[b.status] !== undefined) counts[b.status]++; });
      setTabCounts(counts);
    }).catch(function() {});
  }

  function submitReject(bugId) {
    var updates = { status: 'open', qa_result: 'not_fixed' };
    if (rejectNotes.trim()) updates.fix_notes = rejectNotes.trim();
    fetch('/api/bugs/' + bugId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    }).then(function() {
      if (rejectShot) {
        return fetch('/api/bugs/' + bugId + '/screenshot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: rejectShot })
        });
      }
    }).then(function() {
      setRejectId(null);
      setRejectNotes('');
      setRejectShot(null);
      loadBugs();
      loadTabCounts();
    });
  }

  function captureImage(blob) {
    var reader = new FileReader();
    reader.onload = function(ev) {
      setScreenshot(ev.target.result.split(',')[1]);
    };
    reader.readAsDataURL(blob);
  }

  function handlePaste(e) {
    var items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (var i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        captureImage(items[i].getAsFile());
        e.preventDefault();
        break;
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    var files = e.dataTransfer && e.dataTransfer.files;
    if (!files || !files[0] || files[0].type.indexOf('image') === -1) return;
    captureImage(files[0]);
  }

  if (!visible && !props.standalone) return null;

  var dark = isDarkTheme();
  var bg      = dark ? '#1A1A2E' : '#FFFFFF';
  var surface = dark ? '#222240' : '#F8F9FA';
  var border  = dark ? '#2D2D50' : '#E5E7EB';
  var text    = dark ? '#E8E8F0' : '#1A1A2E';
  var muted   = dark ? '#8B8FA3' : '#6B7280';

  var panelStyle = props.standalone ? {
    width: '100%', height: '100vh', background: bg,
    display: 'flex', flexDirection: 'column', fontFamily: FONT
  } : {
    position: 'fixed', top: 0, right: 0, width: 480, height: '100vh',
    background: bg, borderLeft: '1px solid ' + border, zIndex: 9999,
    display: 'flex', flexDirection: 'column', fontFamily: FONT,
    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)'
  };

  return (
    <div style={panelStyle}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid ' + border,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: text }}>Bug Panel</span>
          {!props.standalone && (
            <button onClick={function() { window.open('/bugs', 'bugpanel', 'width=440,height=800,menubar=no,toolbar=no,location=no,status=no'); if (onClose) onClose(); }} style={{ background: 'none', border: 'none', fontSize: 13, color: muted, cursor: 'pointer', padding: '2px 4px' }} title="Pop out to window">&#x2197;</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select value={filterProject} onChange={function(e) { setFilterProject(e.target.value); }}
            style={{ fontSize: 11, fontFamily: MONO, padding: '3px 6px', borderRadius: 4, border: '1px solid ' + border, background: surface, color: text }}>
            <option value="all">All Projects</option>
            <option value="hub">Hub</option>
            <option value="si">SuperInsight</option>
            <option value="ocp">OCP</option>
            <option value="sm-website">SM Website</option>
            <option value="sc-website">SC Website</option>
            <option value="weekwell">Weekwell</option>
          </select>
          <select value={filterType} onChange={function(e) { setFilterType(e.target.value); }}
            style={{ fontSize: 11, fontFamily: MONO, padding: '3px 6px', borderRadius: 4, border: '1px solid ' + border, background: surface, color: text }}>
            <option value="all">All Types</option>
            <option value="bug">Bugs</option>
            <option value="feature">Features</option>
            <option value="ux">UX</option>
            <option value="task">Tasks</option>
          </select>
          <select value={filterAssignee} onChange={function(e) { setFilterAssignee(e.target.value); }}
            style={{ fontSize: 11, fontFamily: MONO, padding: '3px 6px', borderRadius: 4, border: '1px solid ' + border, background: surface, color: text }}>
            <option value="all">All Assignees</option>
            <option value="aaron@sprintmode.co">Aaron</option>
            <option value="etomaso@sprintmode.co">Eugenio</option>
            <option value="nikola@sprintmode.co">Nikola</option>
            <option value="dani@sprintmode.co">Dani</option>
            <option value="hugh@sprintmode.co">Hugh</option>
            <option value="elle@sprintmode.co">Elle</option>
            <option value="clayton@sprintmode.co">Clayton</option>
          </select>
          {!props.standalone && (
            <button onClick={onClose}
              style={{ background: 'none', border: '1px solid ' + border, borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: muted, cursor: 'pointer', padding: 0, flexShrink: 0 }}
              title="Close bug panel">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid ' + border, flexShrink: 0 }}>
        {TABS.map(function(t) {
          var active = tab === t.id;
          var count = tabCounts[t.status] || 0;
          return (
            <button key={t.id} onClick={function() { setTab(t.id); }}
              style={{
                flex: 1, padding: '8px 4px', fontSize: 11, fontFamily: MONO,
                fontWeight: active ? 700 : 400,
                color: active ? ACCENT : muted,
                background: 'none', border: 'none',
                borderBottom: active ? '2px solid ' + ACCENT : '2px solid transparent',
                cursor: 'pointer'
              }}>
              {t.label}{count > 0 ? ' (' + count + ')' : ''}
            </button>
          );
        })}
      </div>

      {/* Bug List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 20, color: muted, fontSize: 12 }}>Loading...</div>
        )}
        {!loading && bugs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: muted, fontSize: 13 }}>
            {EMPTY_MSGS[tab] || 'No items.'}
          </div>
        )}
        {!loading && bugs.map(function(bug) {
          var dotColor = STATUS_COLORS[bug.status] || '#6B7280';
          return (
            <div key={bug.id} style={{
              background: surface, border: '1px solid ' + border,
              borderRadius: 8, padding: '10px 12px', marginBottom: 6
            }}>
              {/* Meta row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontFamily: MONO, color: muted, textTransform: 'uppercase' }}>
                  {bug.item_type || bug.type || 'bug'}
                </span>
                <span style={{ fontSize: 10, fontFamily: MONO, color: muted }}>
                  {bug.project || 'hub'}
                </span>
                {bug.assigned_agent && <span style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: '#4F6EF7' }}>{bug.assigned_agent.split('@')[0]}</span>}
                <span style={{ fontSize: 10, fontFamily: MONO, color: dotColor, marginLeft: 'auto' }}>
                  {bug.status}
                </span>
              </div>

              {/* Title */}
              {editId === bug.id ? (
                <div style={{ marginBottom: 4 }}>
                  <input value={editTitle} onChange={function(e) { setEditTitle(e.target.value); }}
                    style={{ width: '100%', fontSize: 13, fontWeight: 600, color: text, background: 'transparent', border: '1px solid ' + ACCENT, borderRadius: 4, padding: '3px 6px', fontFamily: FONT, outline: 'none', boxSizing: 'border-box' }} />
                  <textarea value={editDesc} onChange={function(e) { setEditDesc(e.target.value); }}
                    rows={3} style={{ width: '100%', fontSize: 12, color: muted, background: 'transparent', border: '1px solid ' + border, borderRadius: 4, padding: '3px 6px', fontFamily: FONT, outline: 'none', marginTop: 4, resize: 'vertical', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button onClick={function() { updateBug(bug.id, { title: editTitle, description: editDesc }); setEditId(null); }}
                      style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: 'none', background: ACCENT, color: '#fff', cursor: 'pointer', fontFamily: MONO }}>Save</button>
                    <button onClick={function() { setEditId(null); }}
                      style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid ' + border, background: 'transparent', color: muted, cursor: 'pointer', fontFamily: MONO }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: text, flex: 1 }}>{bug.title}</div>
                  <span onClick={function() { setEditId(bug.id); setEditTitle(bug.title || ''); setEditDesc(bug.description || ''); }}
                    style={{ cursor: 'pointer', fontSize: 11, color: muted, flexShrink: 0 }} title="Edit">&#9998;</span>
                </div>
              )}

              {/* Description (only when not editing) */}
              {editId !== bug.id && bug.description && (
                <div style={{ fontSize: 12, color: muted, marginBottom: 6, lineHeight: 1.4 }}>{bug.description}</div>
              )}

              {/* Screenshot */}
              {bug.screenshot_url && (
                <img src={bug.screenshot_url} alt="screenshot"
                  style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 6, border: '1px solid ' + border, display: 'block' }} />
              )}

              {/* Page URL */}
              {bug.page_url && (
                <div style={{ fontSize: 10, fontFamily: MONO, color: muted, marginBottom: 6 }}>{bug.page_url}</div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {tab === 'queue' && (
                  <>
                    <button onClick={function() { updateBug(bug.id, { status: 'triaged' }); }}
                      style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: '1px solid ' + ACCENT, background: 'transparent', color: ACCENT, cursor: 'pointer', fontFamily: MONO }}>
                      Send to EM
                    </button>
                    <button onClick={function() { updateBug(bug.id, { status: 'closed' }); }}
                      style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: '1px solid ' + border, background: 'transparent', color: muted, cursor: 'pointer', fontFamily: MONO }}>
                      Close
                    </button>
                  </>
                )}
                {tab === 'ready' && (
                  <>
                    <button onClick={function() { updateBug(bug.id, { status: 'fixing' }); }}
                      style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: 'none', background: ACCENT, color: '#fff', cursor: 'pointer', fontFamily: MONO, fontWeight: 600 }}>
                      Fire Session
                    </button>
                    {bug.session_prompt && (
                      <button onClick={function() { navigator.clipboard.writeText(bug.session_prompt); }}
                        style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: '1px solid ' + border, background: 'transparent', color: muted, cursor: 'pointer', fontFamily: MONO }}>
                        Copy Prompt
                      </button>
                    )}
                  </>
                )}
                {tab === 'verify' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={function() { updateBug(bug.id, { status: 'closed', qa_result: 'verified' }); }}
                        style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: 'none', background: '#22C55E', color: '#fff', cursor: 'pointer', fontFamily: MONO, fontWeight: 600 }}>
                        Fixed
                      </button>
                      {rejectId !== bug.id && (
                        <button onClick={function() { setRejectId(bug.id); setRejectNotes(''); setRejectShot(null); }}
                          style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontFamily: MONO, fontWeight: 600 }}>
                          Not Fixed
                        </button>
                      )}
                    </div>
                    {rejectId === bug.id && (
                      <div style={{ padding: 8, background: dark ? '#1A1A2E' : '#F8F9FA', borderRadius: 6, border: '1px solid ' + border }}
                        onPaste={function(e) {
                          var items = e.clipboardData && e.clipboardData.items;
                          if (!items) return;
                          for (var i = 0; i < items.length; i++) {
                            if (items[i].type.indexOf('image') !== -1) {
                              var blob = items[i].getAsFile();
                              var reader = new FileReader();
                              reader.onload = function(ev) { setRejectShot(ev.target.result.split(',')[1]); };
                              reader.readAsDataURL(blob);
                              e.preventDefault();
                              break;
                            }
                          }
                        }}>
                        <textarea value={rejectNotes} onChange={function(e) { setRejectNotes(e.target.value); }}
                          placeholder="What did you see? Paste screenshot here too (Cmd+V)"
                          rows={2} autoFocus
                          style={{ width: '100%', padding: 6, fontSize: 11, fontFamily: FONT, borderRadius: 4, border: '1px solid ' + border, background: dark ? '#222240' : '#FFFFFF', color: dark ? '#E8E8F0' : '#1A1A2E', resize: 'vertical', marginBottom: 6, boxSizing: 'border-box' }} />
                        {rejectShot && (
                          <div style={{ marginBottom: 6, position: 'relative' }}>
                            <img src={'data:image/png;base64,' + rejectShot} style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid ' + border }} />
                            <button onClick={function() { setRejectShot(null); }}
                              style={{ position: 'absolute', top: 2, right: 2, background: '#EF4444', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>x</button>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={function() { submitReject(bug.id); }}
                            style={{ fontSize: 10, padding: '4px 10px', borderRadius: 4, border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontFamily: MONO, fontWeight: 600 }}>
                            Submit Not Fixed
                          </button>
                          <button onClick={function() { setRejectId(null); setRejectNotes(''); setRejectShot(null); }}
                            style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: '1px solid ' + border, background: 'transparent', color: dark ? '#8B8FA3' : '#6B7280', cursor: 'pointer', fontFamily: MONO }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fix notes (verify tab) */}
              {tab === 'verify' && bug.fix_notes && (
                <div style={{ fontSize: 11, color: muted, marginTop: 6, padding: '6px 8px', background: bg, borderRadius: 4, fontFamily: MONO }}>
                  {bug.fix_notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Form or Add Button */}
      {showForm ? (
        <div style={{ borderTop: '1px solid ' + border, padding: 12, background: surface, flexShrink: 0 }}
          onPaste={handlePaste} onDrop={handleDrop} onDragOver={function(e) { e.preventDefault(); }} ref={dropRef}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select value={itemType} onChange={function(e) { setItemType(e.target.value); }}
              style={{ fontSize: 11, fontFamily: MONO, padding: '4px 6px', borderRadius: 4, border: '1px solid ' + border, background: bg, color: text, flex: 1 }}>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="ux">UX Issue</option><option value="task">Task</option>
            </select>
            <select value={project} onChange={function(e) { setProject(e.target.value); }}
              style={{ fontSize: 11, fontFamily: MONO, padding: '4px 6px', borderRadius: 4, border: '1px solid ' + border, background: bg, color: text, flex: 1 }}>
              <option value="hub">Hub</option>
              <option value="si">SuperInsight</option>
              <option value="ocp">OCP</option>
              <option value="sm-website">SM Website</option>
              <option value="sc-website">SC Website</option>
              <option value="weekwell">Weekwell</option>
            </select>
            <select value={assignee} onChange={function(e) { setAssignee(e.target.value); }}
              style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", padding: '4px 6px', borderRadius: 4, border: '1px solid ' + border, background: bg, color: text, flex: 1 }}>
              <option value="">Assign to...</option>
              <option value="aaron@sprintmode.co">Aaron</option>
              <option value="etomaso@sprintmode.co">Eugenio</option>
              <option value="nikola@sprintmode.co">Nikola</option>
              <option value="dani@sprintmode.co">Dani</option>
              <option value="hugh@sprintmode.co">Hugh</option>
              <option value="elle@sprintmode.co">Elle</option>
              <option value="clayton@sprintmode.co">Clayton</option>
            </select>
          </div>
          <input value={title} onChange={function(e) { setTitle(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) submitBug(); }}
            placeholder="Bug title"
            autoFocus
            style={{
              width: '100%', padding: 8, fontSize: 13, borderRadius: 6,
              border: '1px solid ' + border, background: bg, color: text,
              fontFamily: FONT, marginBottom: 6, boxSizing: 'border-box'
            }} />
          <textarea value={desc} onChange={function(e) { setDesc(e.target.value); }}
            onPaste={handlePaste}
            placeholder="Description (paste or drop screenshot here)"
            rows={3}
            style={{
              width: '100%', padding: 8, fontSize: 12, borderRadius: 6,
              border: '1px solid ' + border, background: bg, color: text,
              fontFamily: FONT, resize: 'vertical', marginBottom: 6, boxSizing: 'border-box'
            }} />
          {screenshot && (
            <div style={{ marginBottom: 6, position: 'relative' }}>
              <img src={'data:image/png;base64,' + screenshot} alt="screenshot"
                style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid ' + border, display: 'block' }} />
              <button onClick={function() { setScreenshot(null); }}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: '#EF4444', color: '#fff', border: 'none',
                  borderRadius: '50%', width: 20, height: 20,
                  fontSize: 12, cursor: 'pointer', lineHeight: 1
                }}>x</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={submitBug} disabled={submitting || !title.trim()}
              style={{
                flex: 1, padding: 8, borderRadius: 6, border: 'none',
                background: ACCENT, color: '#fff', fontSize: 12,
                fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
                opacity: (submitting || !title.trim()) ? 0.6 : 1
              }}>
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button onClick={resetForm}
              style={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid ' + border,
                background: 'transparent', color: muted, fontSize: 12,
                cursor: 'pointer', fontFamily: FONT
              }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ borderTop: '1px solid ' + border, padding: '8px 12px', flexShrink: 0 }}>
          <button onClick={function() { setShowForm(true); }}
            style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: '1.5px dashed ' + border, background: 'transparent',
              color: ACCENT, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: FONT
            }}>
            + Report Bug or Feature
          </button>
        </div>
      )}
    </div>
  );
}
